import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import config from '../../config';
import { ensureCurrentUser } from '../../util/data';
import {
    Page,
    LayoutSingleColumn,
    LayoutWrapperTopbar,
    LayoutWrapperMain,
    LayoutWrapperFooter,
    Footer,
    LazySection,
} from '../../components';
import { TopbarContainer } from '../../containers';
import css from './LandingPage.css';
import {
    SectionHero,
    SectionHeroLoggedOut,
    SectionOurMission,
    SectionCommunity,
    SectionNewListingsNearby,
    SectionCommunityFeedbacks,
    SectionFAQ,
    SectionJoinUs,
    SectionQuickSearch,
    SectionAcceptedTransactions,
    SectionListings,
    SectionBestSuitsYouHorses,
    SectionBestSuitsYouRiders,
    SectionNewActivities,
    SectionTestimonials,
    SectionPrizesOfMonth,
} from './sections';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getCurrentUserLocation, getLastSeenListings } from '../../util/localStorage';

const { siteTitle, userTypeRider, userTypeHorseowner, listingTypeHorse, listingTypeRider } = config;

export const LandingPageComponent = ({
    intl,
    scrollingDisabled,
    currentUser,
    currentUserRequestInProgress,
    location,
    history,
}) => {
    const [isMobile, , computing, viewport] = useIsMobile(768);
    const isTablet = viewport <= 1024;

    // Schema for search engines (helps them to understand what this page is about)
    // http://schema.org
    // We are using JSON-LD format
    const schemaTitle = intl.formatMessage({ id: 'LandingPage.schemaTitle' }, { siteTitle });
    const schemaDescription = intl.formatMessage({ id: 'LandingPage.schemaDescription' });

    const currentUserDefined = currentUser && currentUser.id;
    const { userType, city, postalCode } = currentUser.attributes.profile.publicData;
    const { city: resolvedCity, postalCode: resolvedPostalCode } = getCurrentUserLocation();

    const isRider = userType === userTypeRider;
    const isOwner = userType === userTypeHorseowner;

    const publicAddress =
        city && postalCode
            ? `${postalCode} ${city}`
            : [resolvedPostalCode, resolvedCity].filter(s => Boolean(s)).join(' ');
    const listingType = isRider ? listingTypeHorse : listingTypeRider;

    const userPending = currentUserRequestInProgress || computing;

    const loggedOutSections = (
        <React.Fragment>
            <SectionHeroLoggedOut history={history} />
            <LazySection computing={userPending}>
                <SectionAcceptedTransactions
                    isMobile={isMobile}
                    isTablet={isTablet}
                    computing={computing}
                />
            </LazySection>
            <LazySection computing={userPending}>
                <SectionNewActivities history={history} />
            </LazySection>
            <LazySection computing={computing}>
                <SectionPrizesOfMonth isMobile={isMobile} history={history} />
            </LazySection>
            <LazySection computing={computing}>
                <SectionNewListingsNearby
                    currentUser={currentUser}
                    listingType={listingTypeRider}
                    isMobile={isMobile}
                    history={history}
                />
            </LazySection>
            <LazySection computing={computing}>
                <SectionCommunity history={history} isTablet={isTablet} />
            </LazySection>
            <LazySection computing={computing}>
                <SectionNewListingsNearby
                    currentUser={currentUser}
                    listingType={listingTypeHorse}
                    isMobile={isMobile}
                    history={history}
                />
            </LazySection>
            <LazySection computing={computing}>
                <SectionOurMission history={history} />
            </LazySection>
            <LazySection computing={computing}>
                <SectionTestimonials isMobile={isMobile} history={history} />
            </LazySection>
            <LazySection computing={computing}>
                <SectionCommunityFeedbacks type="horse" history={history} />
            </LazySection>
            <LazySection computing={computing}>
                <SectionFAQ questionsSet="landing" />
            </LazySection>
            <LazySection computing={computing}>
                <SectionJoinUs history={history} />
            </LazySection>
        </React.Fragment>
    );

    const lastSeenListings = getLastSeenListings();
    const lastSeenListingsExist = Array.isArray(lastSeenListings) && lastSeenListings.length > 0;

    const loggedInSections = currentUserDefined && (
        <React.Fragment>
            <SectionHero
                computing={computing}
                isMobile={isTablet}
                location={location}
                history={history}
                userType={currentUserDefined ? userType : null}
                currentUser={currentUser}
                hideSearhBarForNoAuth
            />
            <SectionQuickSearch currentUser={currentUser} history={history} />
            <LazySection computing={userPending}>
                <SectionAcceptedTransactions
                    isMobile={isMobile}
                    isTablet={isTablet}
                    computing={computing}
                />
            </LazySection>
            <LazySection computing={userPending}>
                <SectionNewActivities history={history} />
            </LazySection>

            <LazySection computing={computing}>
                <SectionPrizesOfMonth isMobile={isMobile} history={history} />
            </LazySection>

            {isRider && (
                <LazySection computing={computing}>
                    <SectionBestSuitsYouHorses currentUser={currentUser} />
                </LazySection>
            )}
            {isOwner && (
                <LazySection computing={computing}>
                    <SectionBestSuitsYouRiders currentUser={currentUser} />
                </LazySection>
            )}

            <LazySection computing={computing}>
                <SectionListings
                    currentUser={currentUser}
                    rootClassName={css.slider}
                    headingId="SectionListingsNearYourLocation.heading"
                    descriptionId={`SectionListingsNearYourLocation.description-for-${userType}`}
                    descriptionValues={{
                        userLocation: publicAddress,
                    }}
                    params={{
                        per_page: 20,
                        pub_type: listingType,
                    }}
                />
            </LazySection>

            {lastSeenListingsExist && (
                <LazySection computing={computing}>
                    <SectionListings
                        currentUser={currentUser}
                        headingId="SectionLastSeen.heading"
                        descriptionId={`SectionLastSeen.description-for-${userType}`}
                        sorter={({ id: { uuid: uuid_1 } }, { id: { uuid: uuid_2 } }) =>
                            lastSeenListings.indexOf(uuid_2) - lastSeenListings.indexOf(uuid_1)
                        }
                        params={{
                            ids: lastSeenListings,
                            pub_type: listingType,
                        }}
                    />
                </LazySection>
            )}
        </React.Fragment>
    );

    return (
        <Page
            scrollingDisabled={scrollingDisabled}
            contentType="website"
            description={schemaDescription}
            title={schemaTitle}
            facebookImages={[
                {
                    url: 'https://horsedeal.imgix.net/static/social-image-1200x630.webp',
                    width: 1200,
                    height: 630,
                },
            ]}
            twitterImages={[
                {
                    url: 'https://horsedeal.imgix.net/static/social-image-600x314.webp',
                    width: 600,
                    height: 314,
                },
            ]}
            schema={{
                '@context': 'http://schema.org',
                '@type': 'WebPage',
                description: schemaDescription,
                name: schemaTitle,
                image: ['https://horsedeal.imgix.net/static/social-image-1200x630.webp'],
            }}
        >
            <LayoutSingleColumn>
                <LayoutWrapperTopbar>
                    <TopbarContainer />
                </LayoutWrapperTopbar>
                <LayoutWrapperMain
                    className={css.layout}
                    blobBackground
                    blobProps={{
                        xDistance: 20,
                        baseWidth: 760,
                    }}
                >
                    <main
                        className={css.container}
                        // key={appIsMounted.toString()}
                    >
                        {currentUserDefined ? loggedInSections : loggedOutSections}
                    </main>
                </LayoutWrapperMain>
                <LayoutWrapperFooter>
                    <Footer />
                </LayoutWrapperFooter>
            </LayoutSingleColumn>
        </Page>
    );
};

const mapStateToProps = ({ user: { currentUser, currentUserRequestInProgress }, UI }) => ({
    currentUser: ensureCurrentUser(currentUser),
    scrollingDisabled: isScrollingDisabled({ UI }),
    currentUserRequestInProgress,
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671

export default compose(withRouter, connect(mapStateToProps), injectIntl)(LandingPageComponent);
